import { FC, useState } from "react";
import { clsx } from "clsx";
import { User } from "../../types/UserGroup";
import { handleError } from "../ErrorHandling";
import { useUsers } from "../useUsers";
import { EditableField } from "./EditableField";
import { SearchPopover } from "./SearchPopover";
import { UserList } from "./UserList";
import styles from "./OwnerCell.module.less";

interface OwnerCellProps {
    ownerId?: number;
    /** The owner's name when the caller has it; looked up among the org's users otherwise. */
    ownerName?: string;
    canEdit?: boolean;
    onSave?: (newOwnerId: number) => Promise<void>;
    onUpdate?: () => void;
}

const hasOwner = (ownerId?: number): ownerId is number => ownerId != null && ownerId !== -1;

/**
 * The owner of a project (or an order form): their name, or a placeholder
 * when unassigned. When the user can edit, hovering reveals an edit
 * affordance and clicking opens a picker with a search box over the org's
 * users. Read-only otherwise.
 */
export const OwnerCell: FC<OwnerCellProps> = ({
    ownerId,
    ownerName,
    canEdit = true,
    onSave,
    onUpdate,
}) => {
    const [open, setOpen] = useState(false);
    // The picker's popover is mounted on first hover or click rather than up
    // front: a list mounts one owner cell per row, and a hundred popovers
    // nobody has touched are what made it slow to paint.
    const [armed, setArmed] = useState(false);
    const users = useUsers();

    const name = hasOwner(ownerId)
        ? (ownerName ?? users?.find((u) => u.id === ownerId)?.name)
        : undefined;
    const trigger = (
        <EditableField canEdit={canEdit} fill>
            <span className={clsx(styles.name, !name && styles.unset)}>{name ?? "Unassigned"}</span>
        </EditableField>
    );

    if (!canEdit) return trigger;

    if (!armed) {
        return (
            <span
                className={styles.anchor}
                onMouseEnter={() => setArmed(true)}
                onFocus={() => setArmed(true)}
                onClick={(e) => {
                    // A row around the field may navigate on click; the picker's clicks must not.
                    e.stopPropagation();
                    setArmed(true);
                    setOpen(true);
                }}
            >
                {trigger}
            </span>
        );
    }

    const select = async (user: User) => {
        setOpen(false);
        if (user.id === ownerId) return;
        try {
            await onSave?.(user.id);
            onUpdate?.();
        } catch (error) {
            handleError(error);
        }
    };

    return (
        <SearchPopover
            open={open}
            onOpenChange={setOpen}
            trigger={trigger}
            placeholder="Search people…"
        >
            {(query) => <UserList selectedId={ownerId} onSelect={select} query={query} />}
        </SearchPopover>
    );
};
